// Reading client lists from CSV / XLS / XLSX and turning each row into an order.
import { CONTACT_METHOD_VALUES, ORDER_STATUSES } from '@/shared/constants';
import { PLAN_VALUES, TELLY, newCode, quote, termsFor } from '@/shared/pricing';

export const MAX_IMPORT_ROWS = 2000;
export const ACCEPT = '.csv,.xls,.xlsx,text/csv,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

/** Columns the importer understands. `aliases` are matched against the file's headers, first match wins. */
export const IMPORT_COLUMNS = [
  { key: 'code', label: 'Order code', group: 'Order', aliases: ['order code', 'code', 'order', 'order id', 'order #', 'reference', 'ref'] },
  { key: 'name', label: 'Client name', group: 'Order', aliases: ['name', 'full name', 'client', 'client name', 'customer', 'customer name'], required: true },
  { key: 'email', label: 'Email', group: 'Order', aliases: ['email', 'e-mail', 'email address'] },
  { key: 'plan', label: 'Service', group: 'Package', aliases: ['service', 'plan', 'package', 'product'], required: true },
  { key: 'term', label: 'Term', group: 'Package', aliases: ['term', 'duration', 'length', 'period'] },
  { key: 'connections', label: 'Connections', group: 'Package', aliases: ['connections', 'connection', 'screens', 'devices', 'max connections'] },
  { key: 'credits', label: 'Credits used', group: 'Package', aliases: ['credits used', 'credits', 'credit', 'panel credits'] },
  { key: 'cost', label: 'Cost (USD)', group: 'Package', aliases: ['cost (usd)', 'cost', 'panel cost', 'credit cost', 'credits cost'] },
  { key: 'price', label: 'Price (USD)', group: 'Package', aliases: ['price (usd)', 'price', 'amount', 'amount (usd)', 'total'] },
  { key: 'start', label: 'Start date', group: 'Dates', aliases: ['start date', 'start', 'activated', 'activated on', 'started'] },
  { key: 'end', label: 'Expiry date', group: 'Dates', aliases: ['expiry date', 'expiry', 'expires', 'end date', 'ends', 'expiration', 'paid through', 'valid until'] },
  { key: 'status', label: 'Status', group: 'Status', aliases: ['status', 'state', 'line status'] },
  { key: 'paymentStatus', label: 'Payment status', group: 'Status', aliases: ['payment status', 'paid?', 'payment'] },
  { key: 'method', label: 'Payment method', group: 'Status', aliases: ['payment method', 'method', 'paid with', 'paid via', 'payment type'] },
  { key: 'preferred', label: 'Preferred contact', group: 'Contact', aliases: ['preferred contact', 'preferred', 'contact method', 'contact via'] },
  { key: 'handle', label: 'Contact handle', group: 'Contact', aliases: ['contact handle', 'handle'] },
  { key: 'discord', label: 'Discord', group: 'Contact', aliases: ['discord'] },
  { key: 'whatsapp', label: 'WhatsApp', group: 'Contact', aliases: ['whatsapp', 'whats app'] },
  { key: 'telegram', label: 'Telegram', group: 'Contact', aliases: ['telegram'] },
  { key: 'phone', label: 'Phone', group: 'Contact', aliases: ['phone', 'phone number', 'mobile', 'cell'] },
  { key: 'otherContact', label: 'Other contact', group: 'Contact', aliases: ['other contact', 'other', 'contact'] },
  { key: 'username', label: 'Line username', group: 'Line', aliases: ['line username', 'username', 'user', 'login'] },
  { key: 'password', label: 'Line password', group: 'Line', aliases: ['line password', 'password', 'pass'] },
  { key: 'server', label: 'Line server', group: 'Line', aliases: ['line server', 'server', 'host', 'url', 'portal', 'dns'] },
  { key: 'device', label: 'Device', group: 'Line', aliases: ['device', 'app'] },
  { key: 'notes', label: 'Notes', group: 'Line', aliases: ['notes', 'note', 'comments', 'comment', 'memo'] },
];

export const COLUMN_GROUPS = ['Order', 'Package', 'Dates', 'Status', 'Contact', 'Line'];

const norm = (s) =>
  String(s ?? '')
    .replace(/^﻿/, '')
    .trim()
    .toLowerCase()
    .replace(/\s*\*+$/, '')
    .replace(/\s*\(required\)$/, '')
    .replace(/\s+/g, ' ');

/** { key: headerIndex } for every column found in the header row. */
export function autoMap(headers) {
  const normalized = headers.map(norm);
  const used = new Set();
  const map = {};
  IMPORT_COLUMNS.forEach((col) => {
    for (const alias of col.aliases) {
      const i = normalized.findIndex((h, idx) => h === alias && !used.has(idx));
      if (i !== -1) {
        map[col.key] = i;
        used.add(i);
        return;
      }
    }
  });
  return map;
}

/** RFC 4180 CSV -> array of rows. Handles quotes, escaped quotes, CRLF and ; or tab separators. */
export function parseCsv(text) {
  const src = String(text ?? '').replace(/^﻿/, '');
  const firstLine = src.split(/\r?\n/, 1)[0] ?? '';
  const counts = [',', ';', '\t'].map((d) => [d, firstLine.split(d).length]);
  const delim = counts.sort((a, b) => b[1] - a[1])[0][0];
  const out = [];
  let row = [];
  let cell = '';
  let quoted = false;
  for (let i = 0; i < src.length; i += 1) {
    const ch = src[i];
    if (quoted) {
      if (ch === '"' && src[i + 1] === '"') {
        cell += '"';
        i += 1;
      } else if (ch === '"') {
        quoted = false;
      } else {
        cell += ch;
      }
    } else if (ch === '"') {
      quoted = true;
    } else if (ch === delim) {
      row.push(cell);
      cell = '';
    } else if (ch === '\n' || ch === '\r') {
      if (ch === '\r' && src[i + 1] === '\n') i += 1;
      row.push(cell);
      out.push(row);
      row = [];
      cell = '';
    } else {
      cell += ch;
    }
  }
  if (cell !== '' || row.length) {
    row.push(cell);
    out.push(row);
  }
  return out;
}

const isBlankRow = (r) => !r || r.every((c) => String(c ?? '').trim() === '');

/** Split a grid into { headers, rows } using the first non-empty row as the header. */
export function toTable(grid) {
  const start = grid.findIndex((r) => !isBlankRow(r));
  if (start === -1) return { headers: [], rows: [] };
  const headers = grid[start].map((h) => String(h ?? '').replace(/^﻿/, '').trim());
  const rows = grid.slice(start + 1).filter((r) => !isBlankRow(r));
  return { headers, rows };
}

/**
 * Read a File into { sheets: [{ name, headers, rows }] }.
 * CSV is parsed here so every cell stays text; Excel goes through SheetJS, loaded on demand.
 */
export async function readSpreadsheet(file) {
  const lower = file.name.toLowerCase();
  if (lower.endsWith('.csv') || lower.endsWith('.txt') || file.type === 'text/csv') {
    const text = await file.text();
    return { sheets: [{ name: 'CSV', ...toTable(parseCsv(text)) }] };
  }
  const XLSX = await import('xlsx');
  const wb = XLSX.read(await file.arrayBuffer(), { type: 'array', cellDates: true });
  const sheets = wb.SheetNames.filter((name) => !TEMPLATE_HELP_SHEETS.includes(name)).map((name) => {
    const grid = XLSX.utils.sheet_to_json(wb.Sheets[name], { header: 1, raw: true, defval: '' });
    return { name, ...toTable(grid) };
  }).filter((s) => s.headers.length);
  return { sheets };
}

const pad = (n) => String(n).padStart(2, '0');
const ymd = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

/** Any spreadsheet date -> YYYY-MM-DD, or null. Accepts Date, Excel serials, ISO, M/D/Y, D/M/Y and month names. */
export function parseDay(v) {
  if (v === null || v === undefined || v === '') return null;
  if (v instanceof Date) return Number.isNaN(v.getTime()) ? null : ymd(v);
  if (typeof v === 'number' && v > 20000 && v < 80000) {
    const d = new Date(Math.round((v - 25569) * 86400000));
    return `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}`;
  }
  const s = String(v).trim();
  let m = s.match(/^(\d{4})[-/.](\d{1,2})[-/.](\d{1,2})/);
  if (m) return `${m[1]}-${pad(m[2])}-${pad(m[3])}`;
  m = s.match(/^(\d{1,2})[-/.](\d{1,2})[-/.](\d{2,4})$/);
  if (m) {
    let a = Number(m[1]);
    let b = Number(m[2]);
    const y = m[3].length === 2 ? 2000 + Number(m[3]) : Number(m[3]);
    // month first unless the first number cannot be a month
    if (a > 12) [a, b] = [b, a];
    if (a < 1 || a > 12 || b < 1 || b > 31) return null;
    return `${y}-${pad(a)}-${pad(b)}`;
  }
  const t = Date.parse(s);
  return Number.isNaN(t) ? null : ymd(new Date(t));
}

/** YYYY-MM-DD -> ISO at local noon, so the day never shifts. */
export const dayIso = (day) => (day ? new Date(`${day}T12:00:00`).toISOString() : null);

export function parseMoney(v) {
  if (v === null || v === undefined || v === '') return null;
  if (typeof v === 'number') return Number.isFinite(v) ? v : null;
  const n = Number(String(v).replace(/[^0-9.-]/g, ''));
  return Number.isFinite(n) && String(v).match(/\d/) ? n : null;
}

const text = (v) => {
  if (v === null || v === undefined) return '';
  if (v instanceof Date) return ymd(v);
  return String(v).trim();
};

export function normalizePlan(v) {
  const s = norm(v);
  if (!s) return null;
  if (s.includes('telly')) return 'Telly';
  return PLAN_VALUES.find((p) => p.toLowerCase() === s) ?? null;
}

export function normalizeMethod(v) {
  const s = norm(v).replace(/[\s_-]/g, '');
  if (!s) return null;
  if (/stripe|card|credit|debit|visa|master|amex|applepay|googlepay/.test(s)) return 'Card';
  if (s.includes('paypal')) return 'PayPal'; // retired, kept so older orders import as they were
  if (s.includes('wise')) return 'Wise';
  if (/crypto|btc|bitcoin|usdt|usdc|eth|ltc|tether/.test(s)) return 'Crypto';
  if (/etransfer|interac|emt/.test(s)) return 'E-Transfer';
  return null;
}

export function normalizeStatus(v) {
  const s = norm(v);
  if (!s) return null;
  const exact = ORDER_STATUSES.find((x) => x.toLowerCase() === s);
  if (exact) return exact;
  if (/expired|lapsed|ended/.test(s)) return 'Active';
  if (/cancel|inactive|disabled|banned|deleted/.test(s)) return 'Cancelled';
  if (/trial|test/.test(s)) return 'Trial';
  if (/invoice|unpaid|due|awaiting/.test(s)) return 'Invoiced';
  if (/pending|new/.test(s)) return 'Pending';
  if (/active|live/.test(s)) return 'Active';
  return null;
}

const isPaid = (v) => /^(paid|yes|y|true|1|complete|completed|received)$/.test(norm(v));

const TERM_WORDS = [
  [/^(1\s*m|1\s*month|monthly|month)$/, 1],
  [/^(3\s*m|3\s*months?|quarterly)$/, 3],
  [/^(6\s*m|6\s*months?)$/, 6],
  [/^(12\s*m|12\s*months?|1\s*y|1\s*year|yearly|annual|annually|year)$/, 12],
  [/^(24\s*m|2\s*y|2\s*years?)$/, 24],
  [/^(36\s*m|3\s*y|3\s*years?)$/, 36],
];

/** The plan's term closest to a length in months. */
function nearestTerm(plan, months) {
  const terms = termsFor(plan).filter((t) => t.months > 0);
  if (!terms.length || !(months > 0)) return null;
  return terms.reduce((best, t) => (Math.abs(t.months - months) < Math.abs(best.months - months) ? t : best)).id;
}

function termFrom(plan, raw, start, end, price, connections) {
  if (plan === 'Telly') return TELLY.term;
  const s = norm(raw);
  if (s) {
    const own = termsFor(plan).find((t) => t.id === s || t.label.toLowerCase() === s);
    if (own) return own.id;
    const word = TERM_WORDS.find(([re]) => re.test(s));
    if (word) return nearestTerm(plan, word[1]);
  }
  // a price that matches exactly one term on the price list settles it
  if (price) {
    const hits = termsFor(plan).filter((t) => quote(plan, t.id, connections) === price);
    if (hits.length === 1) return hits[0].id;
  }
  if (start && end) {
    const days = (new Date(`${end}T12:00:00`) - new Date(`${start}T12:00:00`)) / 86400000;
    return nearestTerm(plan, days / 30.44);
  }
  return null;
}

function contactFrom(get) {
  const handles = {
    Discord: get('discord'),
    WhatsApp: get('whatsapp'),
    Telegram: get('telegram'),
  };
  const preferred = CONTACT_METHOD_VALUES.find((m) => m.toLowerCase() === norm(get('preferred')));
  const handle = get('handle');
  if (preferred && (handles[preferred] || handle)) {
    return { method: preferred, handle: handles[preferred] || handle };
  }
  const first = CONTACT_METHOD_VALUES.find((m) => handles[m]);
  if (first) return { method: first, handle: handles[first] };
  return { method: null, handle: handle || '' };
}

/**
 * One spreadsheet row -> { payload, paid, problems[], warnings[], key }.
 * `payload` is ready for orders_c.create (minus Id); `key` is the line username used to spot duplicates.
 */
export function rowToOrder(row, map, options = {}) {
  const get = (key) => (map[key] === undefined || map[key] === null ? '' : text(row[map[key]]));
  const raw = (key) => (map[key] === undefined || map[key] === null ? '' : row[map[key]]);
  const problems = [];
  const warnings = [];

  const name = get('name');
  if (!name) problems.push('No client name');

  const plan = normalizePlan(get('plan'));
  if (!plan) problems.push(get('plan') ? `Unknown service "${get('plan')}"` : 'No service');

  const start = parseDay(raw('start'));
  const end = parseDay(raw('end'));
  if (get('start') && !start) problems.push(`Can't read start date "${get('start')}"`);
  if (get('end') && !end) problems.push(`Can't read expiry date "${get('end')}"`);
  if (start && end && end < start) problems.push('Expiry is before the start date');

  const connRaw = parseMoney(raw('connections'));
  const maxConn = plan === 'Telly' ? TELLY.maxQty : 5;
  const connections = Math.min(maxConn, Math.max(1, Math.round(connRaw || 1)));
  if (connRaw && connRaw > maxConn) warnings.push(`Connections capped at ${maxConn}`);

  const price = parseMoney(raw('price'));
  if (get('price') && price === null) problems.push(`Can't read price "${get('price')}"`);

  const term = plan ? termFrom(plan, get('term'), start, end, price, connections) : null;
  if (plan && !term) warnings.push('Term guessed as the shortest one');

  const method = normalizeMethod(get('method'));
  if (get('method') && !method) warnings.push(`Payment method "${get('method')}" not recognised`);

  let status = normalizeStatus(get('status'));
  if (get('status') && !status) warnings.push(`Status "${get('status')}" read as Active`);
  if (!status) status = end || start ? 'Active' : 'Pending';

  const paid = map.paymentStatus !== undefined ? isPaid(get('paymentStatus')) : status === 'Active';

  const contact = contactFrom(get);
  const extra = [];
  if (get('phone')) extra.push(`Phone: ${get('phone')}`);
  if (get('otherContact')) extra.push(`Other contact: ${get('otherContact')}`);
  if (norm(get('preferred')) === 'email') extra.push('Prefers email');
  if (get('paymentStatus') && !paid) extra.push(`Payment status: ${get('paymentStatus')}`);
  const notes = [get('notes'), ...extra].filter(Boolean).join('\n');

  const email = get('email');
  if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) problems.push(`Email "${email}" looks wrong`);

  const username = get('username');
  const server = get('server') || options.server || '';

  const payload = {
    Name: get('code') || newCode(),
    full_name_c: name,
    email_c: email || null,
    plan_c: plan,
    term_c: term || (plan ? termsFor(plan)[0]?.id : null),
    connections_c: connections,
    price_c: price ?? 0,
    discount_c: 0,
    currency_c: 'USD',
    payment_method_c: method,
    contact_method_c: contact.method,
    contact_handle_c: contact.handle || null,
    device_c: get('device') || null,
    notes_c: notes || null,
    status_c: status,
    activated_at_c: dayIso(start),
    ends_at_c: plan === 'Telly' ? null : dayIso(end),
    line_server_c: server || null,
    line_username_c: username || null,
    line_password_c: get('password') || null,
    adult_content_c: false,
    wants_trial_c: status === 'Trial',
    include_epg_c: false,
    telly_addon_c: false,
  };

  const creditsUsed = parseMoney(raw('credits'));
  if (get('credits') && (creditsUsed === null || creditsUsed < 0)) warnings.push(`Credits "${get('credits')}" ignored`);
  const cost = parseMoney(raw('cost'));
  if (get('cost') && (cost === null || cost < 0)) warnings.push(`Cost "${get('cost')}" ignored`);

  return {
    payload,
    paid,
    credits: creditsUsed !== null && creditsUsed >= 0 ? creditsUsed : null,
    cost: cost !== null && cost >= 0 ? cost : null,
    start,
    end,
    problems,
    warnings,
    codeGiven: !!get('code'),
    key: username ? username.toLowerCase() : null,
  };
}

/**
 * Key used to match a row to a customer: the email when there is one, otherwise the client's name.
 * Works on an order payload ({ email_c, full_name_c }) and on a customer read back as { email_c } or { full_name_c }.
 */
export function customerKey(payload) {
  const email = String(payload?.email_c ?? '').trim().toLowerCase();
  if (email) return `email:${email}`;
  const name = String(payload?.full_name_c ?? '').trim().replace(/\s+/g, ' ').toLowerCase();
  return name ? `name:${name}` : null;
}

/** customers_c record for a client who is not a customer yet, built from their order row. */
export function rowToCustomer(payload, note) {
  const method = CONTACT_METHOD_VALUES.includes(payload.contact_method_c) ? payload.contact_method_c : null;
  return {
    Name: String(payload.full_name_c ?? '').trim(),
    email_c: payload.email_c ? String(payload.email_c).trim().toLowerCase() : null,
    contact_method_c: method,
    contact_handle_c: method ? payload.contact_handle_c || null : null,
    referral_code_c: newCode('REF-', 6),
    marketing_opt_in_c: false,
    expiry_reminders_c: true,
    notes_c: note || null,
  };
}

/** Demo client list, in the same layout as a typical tracker export. All names are made up. */
export const DEMO_HEADERS = [
  'Name',
  'Service',
  'Connections',
  'Price (USD)',
  'Start date',
  'Expiry date',
  'Status',
  'Payment status',
  'Email',
  'Discord',
  'Telegram',
  'WhatsApp',
  'Preferred contact',
  'Line username',
  'Line password',
  'Payment method',
  'Notes',
];

export const DEMO_ROWS = [
  ['Mara Quill', 'Demon', '2', '56', '2026-10-04', '2027-10-04', 'Active', 'Paid', 'mara.quill@example.com', '', '@maraq', '', 'Telegram', 'demo-maraq', 'Fen9Ruu2', 'Card', 'Two TVs, living room and loft'],
  ['Theo Brandt', 'Wraith', '1', '25', '2026-09-20', '2027-03-20', 'Active', 'Paid', '', 'theo.b', '', '', 'Discord', 'demo-theob', 'Wisp4Lantern', 'Crypto', ''],
  ['Ines Varga', 'Banshee', '3', '45', '2026-10-28', '2027-01-28', 'Active', 'Paid', 'ines.v@example.com', '', '', '+1 555 010 4477', 'WhatsApp', 'demo-inesv', 'Moth7Gable', 'E-transfer', 'Referred by Mara'],
  ['Otto Lind', 'Demon', '1', '4', '2026-11-01', '2026-12-01', 'Invoiced', 'Unpaid', 'otto.lind@example.com', '', '', '', 'Email', 'demo-ottol', 'Cellar3Fog', 'Wise', 'Waiting on Wise'],
  ['Juno Park', 'Telly', '2', '14', '2026-10-15', '', 'Active', 'Paid', 'juno.park@example.com', '', '', '', 'Email', '', '', 'Card', 'Two Desktop Telly licences'],
];

const csvCell = (v) => {
  const s = String(v ?? '');
  return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

export const toCsv = (headers, rows) => [headers, ...rows].map((r) => r.map(csvCell).join(',')).join('\r\n');

function download(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export function downloadSampleCsv() {
  download(new Blob([`﻿${toCsv(DEMO_HEADERS, DEMO_ROWS)}`], { type: 'text/csv;charset=utf-8' }), 'ghost-cable-import-sample.csv');
}

export async function downloadSampleXlsx() {
  const XLSX = await import('xlsx');
  const ws = XLSX.utils.aoa_to_sheet([DEMO_HEADERS, ...DEMO_ROWS]);
  ws['!cols'] = DEMO_HEADERS.map((h) => ({ wch: Math.max(12, h.length + 2) }));
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Clients');
  XLSX.writeFile(wb, 'ghost-cable-import-sample.xlsx');
}

/* ---------- Blank template ---------- */

/** Sheets in the template that only explain things. The importer never reads them. */
export const TEMPLATE_HELP_SHEETS = ['How to fill it in', 'Lists', 'Example'];

/** Header row of the template: every column, required ones marked with *. */
export const TEMPLATE_HEADERS = IMPORT_COLUMNS.map((c) => (c.required ? `${c.label} *` : c.label));

const TERM_HELP = 'Demon: 1 Month, 3 Months, 6 Months, 1 Year, 2 Years, 3 Years · Wraith: 1 Month, 3 Months, 6 Months, 12 Months · Banshee: 1 Month, 3 Months, 6 Months, 1 Year · Telly: always Lifetime';

/** One line per column: what goes in it, what it accepts, and an example. */
export const TEMPLATE_GUIDE = {
  code: ['Leave blank and a new code is made. Fill it in to keep your own codes or to update an order that is already here.', 'Any text', 'GC-4KX9QZ'],
  name: ['Full name of the client.', 'Any text', 'Mara Quill'],
  email: ['Where receipts and reminders go.', 'An email address', 'mara.quill@example.com'],
  plan: ['Which service the client is on.', 'Demon, Wraith, Banshee, Telly', 'Demon'],
  term: ['How long the package runs. Leave blank and it is worked out from the price or the dates.', TERM_HELP, '1 Year'],
  connections: ['How many screens can watch at once. For Telly, the number of licences.', 'Demon and Banshee 1 to 5 · Wraith 1 · Telly 1 to 10', '2'],
  credits: ['Panel credits this order used. Fills in the cost on the ledger from your credit rates.', 'A number', '24'],
  cost: ['What the order cost you, in US dollars. Leave blank and it is worked out from credits x your credit rate.', 'A number, no currency sign needed', '33.60'],
  price: ['What the client paid, in US dollars.', 'A number, no currency sign needed', '56'],
  start: ['The day the line started.', 'A date: 2026-10-04 or 10/04/2026', '2026-10-04'],
  end: ['The day the line runs out. Leave blank for Telly.', 'A date: 2027-10-04 or 10/04/2027', '2027-10-04'],
  status: ['Where the order stands. Leave blank and it is Active when there are dates, otherwise Pending.', 'Pending, Invoiced, Trial, Active, Cancelled', 'Active'],
  paymentStatus: ['Whether the client has paid. Paid rows can get a payment recorded on import.', 'Paid or Unpaid', 'Paid'],
  method: ['How the client paid.', 'Card, Wise, Crypto, E-Transfer', 'Card'],
  preferred: ['How the client likes to be reached.', 'Discord, WhatsApp, Telegram, Email', 'Telegram'],
  handle: ['Their handle for the preferred contact, if you do not use the Discord / WhatsApp / Telegram columns.', 'Any text', '@maraq'],
  discord: ['Discord username.', 'Any text', 'mara.q'],
  whatsapp: ['WhatsApp number.', 'A phone number', '+1 555 010 4477'],
  telegram: ['Telegram username.', 'Any text', '@maraq'],
  phone: ['Phone number. Saved in the order notes.', 'A phone number', '+1 555 010 4477'],
  otherContact: ['Any other way to reach them. Saved in the order notes.', 'Any text', 'Signal: maraq'],
  username: ['Line username. Also used to spot clients that are already here.', 'Any text', 'maraq'],
  password: ['Line password.', 'Any text', 'Fen9Ruu2'],
  server: ['Line server URL. Leave blank to use the one you set on the import screen.', 'A URL', 'http://line.example.com:8080'],
  device: ['What they watch on.', 'Any text', 'Firestick'],
  notes: ['Anything else worth keeping.', 'Any text', 'Two TVs, living room and loft'],
};

/** Allowed values, one column per field, for the Lists sheet. */
export const TEMPLATE_LISTS = [
  ['Service', ...PLAN_VALUES],
  ['Term (Demon)', '1 Month', '3 Months', '6 Months', '1 Year', '2 Years', '3 Years'],
  ['Term (Wraith)', '1 Month', '3 Months', '6 Months', '12 Months'],
  ['Term (Banshee)', '1 Month', '3 Months', '6 Months', '1 Year'],
  ['Term (Telly)', 'Lifetime'],
  ['Status', ...ORDER_STATUSES],
  ['Payment status', 'Paid', 'Unpaid'],
  ['Payment method', 'Card', 'Wise', 'Crypto', 'E-Transfer'],
  ['Preferred contact', ...CONTACT_METHOD_VALUES, 'Email'],
];

const TEMPLATE_EXAMPLES = [
  {
    name: 'Mara Quill', email: 'mara.quill@example.com', plan: 'Demon', term: '1 Year', connections: 2, credits: 24, cost: 33.6, price: 56,
    start: '2026-10-04', end: '2027-10-04', status: 'Active', paymentStatus: 'Paid', method: 'Card', preferred: 'Telegram',
    telegram: '@maraq', username: 'maraq', password: 'Fen9Ruu2', device: 'Firestick', notes: 'Two TVs, living room and loft',
  },
  {
    name: 'Theo Brandt', plan: 'Wraith', term: '6 Months', connections: 1, credits: 6, price: 25, start: '2026-09-20',
    end: '2027-03-20', status: 'Active', paymentStatus: 'Paid', method: 'Crypto', preferred: 'Discord', discord: 'theo.b',
    username: 'theob', password: 'Wisp4Lantern',
  },
  {
    name: 'Otto Lind', email: 'otto.lind@example.com', plan: 'Banshee', term: '1 Month', connections: 1, price: 15,
    start: '2026-11-01', end: '2026-12-01', status: 'Invoiced', paymentStatus: 'Unpaid', method: 'E-Transfer',
    preferred: 'Email', username: 'ottol', password: 'Cellar3Fog', notes: 'Pays at the start of each month',
  },
];

const colWidths = (rows, min = 10, max = 60) =>
  rows[0].map((_, i) => ({ wch: Math.min(max, Math.max(min, ...rows.map((r) => String(r[i] ?? '').length + 2))) }));

/** Blank Excel template: an empty Clients sheet with every column, plus a guide, the allowed values and an example. */
export async function downloadTemplateXlsx() {
  const XLSX = await import('xlsx');
  const wb = XLSX.utils.book_new();
  const last = XLSX.utils.encode_col(TEMPLATE_HEADERS.length - 1);

  // Clients comes first so it is the sheet the importer opens.
  const clients = XLSX.utils.aoa_to_sheet([TEMPLATE_HEADERS]);
  clients['!cols'] = IMPORT_COLUMNS.map((c, i) => ({
    wch: Math.max(TEMPLATE_HEADERS[i].length + 4, ['notes', 'email', 'server'].includes(c.key) ? 28 : 14),
  }));
  clients['!autofilter'] = { ref: `A1:${last}1` };
  XLSX.utils.book_append_sheet(wb, clients, 'Clients');

  const guideRows = [
    ['Column', 'Required', 'What to put in it', 'Accepted values', 'Example'],
    ...IMPORT_COLUMNS.map((c) => {
      const [what, values, example] = TEMPLATE_GUIDE[c.key] ?? ['', '', ''];
      return [c.label, c.required ? 'Yes' : 'No', what, values, example];
    }),
    [],
    ['How it works'],
    ['1. Fill in the Clients sheet, one client per row. Only Client name and Service are required; leave anything you do not know blank.'],
    ['2. Keep the header row as it is. Columns can be moved or deleted, and extra columns are ignored.'],
    ['3. Save, then drop the file on the Import orders page. You see every row checked before anything is saved.'],
    ['This sheet, Lists and Example are never imported.'],
  ];
  const guide = XLSX.utils.aoa_to_sheet(guideRows);
  guide['!cols'] = [{ wch: 18 }, { wch: 10 }, { wch: 70 }, { wch: 60 }, { wch: 30 }];
  XLSX.utils.book_append_sheet(wb, guide, 'How to fill it in');

  const height = Math.max(...TEMPLATE_LISTS.map((l) => l.length));
  const listRows = Array.from({ length: height }, (_, r) => TEMPLATE_LISTS.map((l) => l[r] ?? ''));
  const lists = XLSX.utils.aoa_to_sheet(listRows);
  lists['!cols'] = colWidths(listRows, 14);
  XLSX.utils.book_append_sheet(wb, lists, 'Lists');

  const exampleRows = [TEMPLATE_HEADERS, ...TEMPLATE_EXAMPLES.map((ex) => IMPORT_COLUMNS.map((c) => ex[c.key] ?? ''))];
  const example = XLSX.utils.aoa_to_sheet(exampleRows);
  example['!cols'] = colWidths(exampleRows, 12, 34);
  XLSX.utils.book_append_sheet(wb, example, 'Example');

  XLSX.writeFile(wb, 'ghost-cable-import-template.xlsx');
}

const BOM = String.fromCharCode(0xfeff);

/** Same columns as the Excel template, header row only. */
export function downloadTemplateCsv() {
  download(new Blob([BOM + toCsv(TEMPLATE_HEADERS, [])], { type: 'text/csv;charset=utf-8' }), 'ghost-cable-import-template.csv');
}

export const chunk = (list, size) => {
  const out = [];
  for (let i = 0; i < list.length; i += size) out.push(list.slice(i, i + size));
  return out;
};
